import React, { useState, useEffect } from 'react';
import { Check, ScanLine, Users, CreditCard, LineChart, ChevronRight } from 'lucide-react';
import { dismissGettingStarted, hasSeenInsights } from '../../utils/onboarding';

const PRIMARY = '#2D5BFF';

/**
 * GettingStartedCard — a short checklist that drives first-run feature
 * discovery. Auto-hides when all 4 steps are done (no manual dismiss).
 * When allDone fires it writes the dismiss key so Dashboard's onboardingActive
 * guard deactivates and promo cards reappear.
 */
export default function GettingStartedCard({
  hasReceipts,
  hasCircle,
  hasCard,
  startCollapsed = false,
  onScan,
  onCreateCircle,
  onAddCard,
  onOpenInsights,
}) {
  const [expanded, setExpanded] = useState(!startCollapsed);

  const seenInsights = hasSeenInsights();

  const steps = [
    { key: 'scan',     label: 'Scan your first receipt',          done: !!hasReceipts,   icon: ScanLine,   action: onScan },
    { key: 'circle',   label: 'Create a Circle & invite someone', done: !!hasCircle,     icon: Users,      action: onCreateCircle },
    { key: 'card',     label: 'Add a loyalty card or voucher',    done: !!hasCard,       icon: CreditCard, action: onAddCard },
    { key: 'insights', label: 'Peek at your Insights',            done: !!seenInsights,  icon: LineChart,  action: onOpenInsights },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone   = doneCount === steps.length;

  // Write the dismiss key when all steps complete so Dashboard's
  // onboardingActive guard deactivates and promo cards reappear.
  useEffect(() => {
    if (allDone) dismissGettingStarted();
  }, [allDone]);

  if (allDone) return null;

  // Slim collapsed bar for returning users — expands on tap.
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        data-testid="getting-started-collapsed"
        className="w-full flex items-center gap-3"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: 16,
          padding: '12px 16px',
          boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
          border: '1px solid #F1F5F9',
        }}
      >
        <span
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 30, height: 30, borderRadius: 999,
            background: '#EEF2FF', color: PRIMARY,
            fontSize: 12, fontWeight: 700, fontFamily: 'Manrope, sans-serif',
          }}
        >
          {doneCount}/{steps.length}
        </span>
        <span
          className="flex-1 text-left"
          style={{ fontSize: 14, fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}
        >
          Finish setting up
        </span>
        <ChevronRight className="w-4 h-4" style={{ color: '#9CA3AF' }} />
      </button>
    );
  }

  return (
    <div
      data-testid="getting-started"
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: 16,
        boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
        border: '1px solid #F1F5F9',
      }}
    >
      <div>
        <h3 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Get set up
        </h3>
        <p className="mt-0.5" style={{ color: '#6B7280', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
          {doneCount} of {steps.length} done — a few taps to get the most from Buddy
        </p>
      </div>

      {/* Progress bar */}
      <div className="mt-3" style={{ height: 6, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
        <div
          style={{
            width: `${(doneCount / steps.length) * 100}%`,
            height: '100%',
            background: PRIMARY,
            borderRadius: 999,
            transition: 'width .3s',
          }}
        />
      </div>

      {/* Steps */}
      <div className="mt-3 flex flex-col">
        {steps.map((s) => (
          <button
            key={s.key}
            onClick={() => { if (!s.done && s.action) s.action(); }}
            disabled={s.done}
            data-testid={`gs-step-${s.key}`}
            className="flex items-center gap-3 text-left"
            style={{
              padding: '10px 0',
              borderBottom: '1px solid #F8FAFC',
              cursor: s.done ? 'default' : 'pointer',
              opacity: s.done ? 0.6 : 1,
            }}
          >
            <span
              className="flex items-center justify-center flex-shrink-0"
              style={{ width: 30, height: 30, borderRadius: 999, background: s.done ? '#DCFCE7' : '#EEF2FF' }}
            >
              {s.done
                ? <Check className="w-4 h-4" style={{ color: '#16A34A' }} />
                : <s.icon className="w-4 h-4" style={{ color: PRIMARY }} />
              }
            </span>
            <span
              className="flex-1"
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#111827',
                fontFamily: 'DM Sans, sans-serif',
                textDecoration: s.done ? 'line-through' : 'none',
              }}
            >
              {s.label}
            </span>
            {!s.done && <ChevronRight className="w-4 h-4" style={{ color: '#9CA3AF' }} />}
          </button>
        ))}
      </div>
    </div>
  );
}